// Customer Service for handling customer registration and data operations
// Follows the same pattern as other services in the application
import { sharepointServiceProduction } from '../utils/sharepointServiceProduction'

export interface Customer {
  id: string
  name: string
  email: string
  phone?: string
  address?: string
  preferences?: CustomerPreferences
  registrationDate: string
  lastUpdated: string
  paid?: boolean
}

export interface CustomerPreferences {
  emailNotifications: boolean
  smsNotifications: boolean
  preferredClassTypes: string[]
  notes?: string
}

export interface CustomerRegistrationData {
  name: string
  email: string
  phone?: string
  address?: string
  preferences?: Partial<CustomerPreferences>
}

class CustomerService {
  private readonly STORAGE_KEY = 'salty_mermaid_customers'

  /**
   * Generate a unique customer ID
   */
  private generateCustomerId(): string {
    return `customer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Get all customers from localStorage
   */
  getCustomers(): Customer[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error loading customers:', error)
      return []
    }
  }

  /**
   * Register a new customer
   */
  async registerCustomer(registrationData: CustomerRegistrationData): Promise<Customer> {
    try {
      // Check if customer with this email already exists (case-insensitive)
      const existingCustomers = this.getCustomers()
      const existingCustomer = existingCustomers.find(
        c => c.email.toLowerCase() === registrationData.email.toLowerCase()
      )

      if (existingCustomer) {
        throw new Error('A customer with this email already exists')
      }

      // Create new customer
      const newCustomer: Customer = {
        id: this.generateCustomerId(),
        name: registrationData.name,
        email: registrationData.email,
        phone: registrationData.phone,
        address: registrationData.address,
        preferences: {
          emailNotifications: true,
          smsNotifications: false,
          preferredClassTypes: [],
          ...registrationData.preferences
        },
        registrationDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      }

      // Add to existing customers
      existingCustomers.push(newCustomer)

      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(existingCustomers))

      // Automatically sync to SharePoint
      try {
        await sharepointServiceProduction.syncCustomerItem(newCustomer)
        console.log('✅ Customer registered and synced to SharePoint:', newCustomer.email)
      } catch (syncError) {
        console.warn('⚠️ Customer registered locally but SharePoint sync failed:', syncError)
        // Don't fail the registration if SharePoint sync fails
      }

      console.log('✅ Customer registered successfully:', newCustomer.email)
      return newCustomer

    } catch (error) {
      console.error('❌ Error registering customer:', error)
      throw error
    }
  }

  /**
   * Get customer by email
   */
  getCustomerByEmail(email: string): Customer | null {
    const customers = this.getCustomers()
    return customers.find(c => c.email === email) || null
  }

  /**
   * Get customer by ID
   */
  getCustomerById(id: string): Customer | null {
    const customers = this.getCustomers()
    return customers.find(c => c.id === id) || null
  }

  /**
   * Update customer information
   */
  async updateCustomer(customerId: string, updates: Partial<Customer>): Promise<Customer> {
    try {
      const customers = this.getCustomers()
      const customerIndex = customers.findIndex(c => c.id === customerId)

      if (customerIndex === -1) {
        throw new Error('Customer not found')
      }

      // Update customer
      const updatedCustomer = {
        ...customers[customerIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      }

      customers[customerIndex] = updatedCustomer

      // Save to localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(customers))

      // Automatically sync to SharePoint
      try {
        await sharepointServiceProduction.syncCustomerItem(updatedCustomer)
        console.log('✅ Customer updated and synced to SharePoint:', updatedCustomer.email)
      } catch (syncError) {
        console.warn('⚠️ Customer updated locally but SharePoint sync failed:', syncError)
        // Don't fail the update if SharePoint sync fails
      }

      console.log('✅ Customer updated successfully:', updatedCustomer.email)
      return updatedCustomer

    } catch (error) {
      console.error('❌ Error updating customer:', error)
      throw error
    }
  }

  /**
   * Delete customer
   */
  async deleteCustomer(customerId: string): Promise<void> {
    try {
      const customers = this.getCustomers()
      const customerToDelete = customers.find(c => c.id === customerId)
      const filteredCustomers = customers.filter(c => c.id !== customerId)

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredCustomers))

      // Automatically sync deletion to SharePoint
      if (customerToDelete) {
        try {
          await sharepointServiceProduction.deleteCustomerItem(customerId)
          console.log('✅ Customer deleted locally and from SharePoint:', customerToDelete.email)
        } catch (syncError) {
          console.warn('⚠️ Customer deleted locally but SharePoint deletion failed:', syncError)
          // Don't fail the deletion if SharePoint sync fails
        }
      }

      console.log('✅ Customer deleted successfully')

    } catch (error) {
      console.error('❌ Error deleting customer:', error)
      throw error
    }
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Validate phone format (optional)
   */
  validatePhone(phone?: string): boolean {
    if (!phone) return true // Phone is optional
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/
    return phoneRegex.test(phone.replace(/[-\s\(\)]/g, ''))
  }

  /**
   * Get customer statistics
   */
  getCustomerStats() {
    const customers = this.getCustomers()

    return {
      totalCustomers: customers.length,
      recentRegistrations: customers.filter(c => {
        const registrationDate = new Date(c.registrationDate)
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
        return registrationDate > thirtyDaysAgo
      }).length,
      emailNotificationsEnabled: customers.filter(c => c.preferences?.emailNotifications).length,
      smsNotificationsEnabled: customers.filter(c => c.preferences?.smsNotifications).length
    }
  }
}

// Export singleton instance
export const customerService = new CustomerService()